import React, { useState, useEffect } from "react";
import { Modal, Row, Col, Badge, Button, Form, Spinner } from "react-bootstrap";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCar,
  FaMapMarkerAlt,
  FaStar,
  FaEdit,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { formatDisplayDate } from "../../../utils/helpers";
import { commissionLabel, isCommissionEnabled } from "../../../utils/commission";
import { driverAPI } from "../../../api/apiSystem";
import Swal from "sweetalert2";

const DriverDetailsModal = ({ show, onHide, driverData, type = "booking" }) => {
  const { t } = useTranslation();
  const hasCommission = isCommissionEnabled(driverData);

  const [editingVehicle, setEditingVehicle] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [submittingVehicle, setSubmittingVehicle] = useState(false);

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // Load sub-drivers/vehicles when editing mode is triggered
  useEffect(() => {
    if (editingVehicle && show) {
      setLoadingVehicles(true);
      driverAPI.vehicles
        .index()
        .then((res) => {
          if (res.success) {
            setVehicles(res.data.data || res.data || []);
          } else {
            console.error("Failed to load vehicles:", res.message);
          }
        })
        .catch((err) => console.error("Error loading vehicles:", err))
        .finally(() => setLoadingVehicles(false));
    }
  }, [editingVehicle, show]);

  // Set current vehicle number as initial selection
  useEffect(() => {
    if (driverData && driverData.vehicle_number) {
      setSelectedVehicle(driverData.vehicle_number);
    } else {
      setSelectedVehicle("");
    }
  }, [driverData, show]);

  // Handle transfering vehicle/sub-driver
  const handleTransferVehicle = async () => {
    if (!selectedVehicle) return;
    setSubmittingVehicle(true);
    try {
      const apiCall = type === "transport"
        ? driverAPI.transportsOrders.transfer(driverData.id, { vehicle_number: selectedVehicle })
        : driverAPI.bookingsOrders.transfer(driverData.id, { vehicle_number: selectedVehicle });
        
      const res = await apiCall;
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: t('success', 'Erfolg'),
          text: t('driverCard.messages.vehicleUpdated', 'Zusatzfahrer / Fahrzeug erfolgreich zugewiesen'),
          timer: 2000,
          showConfirmButton: false,
        });
        setEditingVehicle(false);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(res.message);
      }
    } catch(err) {
      Swal.fire({
        icon: "error",
        title: t('error', 'Fehler'),
        text: err.message || t('driverCard.messages.vehicleUpdateFailed', 'Fehler beim Zuweisen des Zusatzfahrers / Fahrzeugs'),
      });
    } finally {
      setSubmittingVehicle(false);
    }
  };

  if (!driverData) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaUser className="me-2" />
          {t("driverCard.driverDetails.driverInfo")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          {/* المعلومات الأساسية */}
          <Col md={6}>
            <div className="p-3 border rounded bg-light h-100">
              <h6 className="fw-bold text-secondary mb-3">
                {t("driverCard.driverDetails.basicInfo")}
              </h6>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.driverName")}:</strong>
                <div className="text-dark">{driverData.driver_name || "-"}</div>
              </div>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.phone")}:</strong>
                <div className="text-dark">
                  <FaPhone className="me-1 text-success" />
                  {driverData.phone_number || "-"}
                </div>
              </div>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.email")}:</strong>
                <div className="text-dark">
                  <FaEnvelope className="me-1 text-primary" />
                  {driverData.driver_email || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* معلومات المركبة */}
          <Col md={6}>
            <div className="p-3 border rounded bg-light h-100">
              <h6 className="fw-bold text-secondary mb-3">
                <FaCar className="me-2" />
                {t("driverCard.driverDetails.vehicleInfo")}
              </h6>

              <div className="mb-2">
                <strong>
                  {t("driverCard.driverDetails.vehicleDriverName")}:
                </strong>
                {!editingVehicle ? (
                  <div className="d-flex align-items-center">
                    <div className="text-dark me-2">{driverData.vehicle_driver_name || "-"}</div>
                    {driverData.vehicle_number && (
                      <Button variant="link" className="p-0 text-primary" onClick={() => setEditingVehicle(true)}>
                        <FaEdit size={16} />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 p-2 border rounded bg-white shadow-sm">
                    {loadingVehicles ? (
                      <div className="text-center"><Spinner animation="border" size="sm" /></div>
                    ) : (
                      <>
                        <Form.Select 
                          size="sm" 
                          className="mb-2"
                          value={selectedVehicle}
                          onChange={(e) => setSelectedVehicle(e.target.value)}
                        >
                          <option value="">{t('selectVehicle') || "Select Driver/Vehicle..."}</option>
                          {vehicles.map(v => (
                            <option key={v.id} value={v.vehicle_number}>
                              {v.user_name || v.driver_name || v.vehicle_driver_name ? `${v.user_name || v.driver_name || v.vehicle_driver_name} - ` : ''}{v.vehicle_type} ({v.vehicle_number})
                            </option>
                          ))}
                        </Form.Select>
                        <div className="d-flex justify-content-end gap-2">
                          <Button variant="secondary" size="sm" onClick={() => setEditingVehicle(false)} disabled={submittingVehicle}>
                            <FaTimes />
                          </Button>
                          <Button variant="success" size="sm" onClick={handleTransferVehicle} disabled={!selectedVehicle || submittingVehicle}>
                            {submittingVehicle ? <Spinner animation="border" size="sm" /> : <FaCheck />}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.vehicleType")}:</strong>
                <div className="text-dark">
                  {driverData.vehicle_type || "-"}
                </div>
              </div>

              {driverData.driving_choice && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.driverDetails.drivingChoice")}:
                  </strong>
                  <Badge bg="info" className="ms-1">
                    {driverData.driving_choice}
                  </Badge>
                </div>
              )}

              {driverData.vehicle_number && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.driverDetails.vehicleNumber")}:
                  </strong>
                  <div className="text-dark">{driverData.vehicle_number}</div>
                </div>
              )}

              <div className="mb-2">
                <strong>
                  {t("driverCard.driverDetails.currentLocation")}:
                </strong>
                <div className="text-dark">
                  <FaMapMarkerAlt className="me-1 text-danger" />
                  {driverData.current_location || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* التقييم والسعر */}
          <Col md={6}>
            <div className="p-3 border rounded bg-light h-100">
              <h6 className="fw-bold text-secondary mb-3">
                <FaStar className="me-2 text-warning" />
                {t("driverCard.driverDetails.ratingAndPrice")}
              </h6>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.averageRating")}:</strong>
                <div className="text-dark">
                  {driverData.average_rating || "0"} ⭐ (
                  {driverData.total_reviews || 0}{" "}
                  {t("driverCard.driverDetails.reviews")})
                </div>
              </div>


              {driverData.price || userType === "driver" && (
                <div className="mb-2">
                  <strong>{t("driverCard.driverDetails.offerPrice")}:</strong>
                  <div className="text-success fw-bold fs-5">
                    {(() => {
                      const rawPrice = driverData.price;
                      if (!rawPrice) return "0,00";
                      const cleanPrice = typeof rawPrice === 'string'
                        ? rawPrice.replace(/\./g, '').replace(',', '.')
                        : rawPrice;
                      const num = Number(cleanPrice);
                      return isNaN(num) ? rawPrice : num.toFixed(2).replace(".", ",");
                    })()} EUR
                  </div>
                </div>
              )}

              {type === "transport" && (!hasCommission || driverData.platform_percentage) && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.driverDetails.platformPercentage")}:
                  </strong>
                  <div className="text-muted">
                    {hasCommission
                      ? `${driverData.platform_percentage} EUR`
                      : commissionLabel}
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* معلومات إضافية */}
          <Col md={6}>
            <div className="p-3 border rounded bg-light h-100">
              <h6 className="fw-bold text-secondary mb-3">
                {t("driverCard.driverDetails.additionalInfo")}
              </h6>

              <div className="mb-2">
                <strong>{t("driverCard.driverDetails.status")}:</strong>
                <Badge
                  bg={
                    driverData.status === t("pending")
                      ? "warning"
                      : driverData.status === t("approved")
                        ? "success"
                        : driverData.status === t("rejected")
                          ? "danger"
                          : "secondary"
                  }
                  className="ms-2"
                >
                  {t(driverData.status) || "-"}
                </Badge>
              </div>

              {driverData.info && (
                <div className="mb-2">
                  <strong>
                    {t("driverCard.driverDetails.additionalNotes")}:
                  </strong>
                  <div className="text-muted small p-2 bg-white rounded">
                    {driverData.info}
                  </div>
                </div>
              )}

              {(driverData.offer_date || driverData.created_at) && (
                <div className="mb-2">
                  <strong>Angebotsdatum Bis:</strong>
                  <div className="text-muted small">
                    {driverData.offer_date ||
                      formatDisplayDate(driverData.created_at)}
                  </div>
                </div>
              )}

              {(driverData.assign_date || driverData.booking?.approved_date) && (
                <div className="mb-2">
                  <strong>{t("driverCard.driverDetails.assignDate")}:</strong>
                  <div className="text-muted small">
                    {(formatDisplayDate(driverData.assign_date) ||
                      driverData.assign_date ||
                      formatDisplayDate(driverData.booking?.approved_date) ||
                      driverData.booking?.approved_date)
                      ?.split(" ")[0]}
                  </div>
                </div>
              )}

              <div className="stages-timeline">
                {driverData.stages?.length > 0 ? (
                  driverData.stages.map((stage) => (
                    <div
                      key={stage.stage_number}
                      className="mb-3 p-3 bg-light rounded"
                    >
                      <div className="fw-bold mb-2 text-dark">
                        Stops {stage.stage_number} — {stage.from_location} →{" "}
                        {stage.to_location}
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <strong className="text-warning">
                            {t("driverRequest.modals.receiveDate")}:
                          </strong>
                          <div className="small text-muted">
                            {formatDisplayDate(stage.received_date) ||
                              driverData.booking?.loading_date_time?.[
                              stage.stage_number - 1
                              ] ||
                              "—"}
                          </div>
                        </div>
                        <div className="col-6">
                          <strong className="text-primary">
                            {t("driverRequest.modals.deliveryDate")}:
                          </strong>
                          <div className="small text-muted">
                            {formatDisplayDate(stage.completed_date) ||
                              driverData.booking?.unloading_date_time?.[
                              stage.stage_number - 1
                              ] ||
                              "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // الحالة القديمة (fallback)
                  <>
                    {(driverData.received_date ||
                      driverData.booking?.loading_date_time?.[0]) && (
                        <div className="mb-2">
                          <strong>
                            {t("driverRequest.modals.receiveDate")}:
                          </strong>
                          <div className="text-muted small">
                            {formatDisplayDate(driverData.received_date) ||
                              driverData.booking?.loading_date_time?.[0]}
                          </div>
                        </div>
                      )}
                    {(driverData.completed_date ||
                      driverData.booking?.unloading_date_time?.[0]) && (
                        <div className="mb-2">
                          <strong>
                            {t("driverRequest.modals.deliveryDate")}:
                          </strong>
                          <div className="text-muted small">
                            {formatDisplayDate(driverData.completed_date) ||
                              driverData.booking?.unloading_date_time?.[0]}
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default DriverDetailsModal;
